import { useCallback } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';

import { Button } from 'ui-components/components/button';
import { Input, version2 } from 'ui-components/components/input';
import { FlexRow } from 'ui-components/components/flex/row';
import { Gap } from 'ui-components/components/flex/gap';
import { NotificationService } from 'ui-components/services/notification-service';
import { useAsyncSafeState } from 'ui-components/hooks/async-safe';
import { useSalesOrderIdContext } from '../../../../../context/sales-order-id-context-provider';
import { useEntitiesSalesOrder } from '../../../../../../hooks/entities/selectors/sales-orders';
import { useReturnDataContext } from '../../../return-data-provider';
import { createReturnNote } from '../../../../../../store/returns/returns.api';

import { messages } from './messages';
import { Segment } from '../../../../../../services/analytics-services/segment';
import { RETURN_ACTION_NOTE_FORM_SUBMITTED } from '../../../../../../constants/analytics-events';
import style from './styles.module.less';

export const NoteForm = () => {
  const [value, setValue] = useAsyncSafeState('');
  const [isProcessing, setIsProcessing] = useAsyncSafeState(false);

  const intl = useIntl();
  const salesOrderId = useSalesOrderIdContext();
  const salesOrder = useEntitiesSalesOrder(salesOrderId) ?? {};
  const { requestDetails, onUpdateRequestDetails } = useReturnDataContext();

  const storeGuid = salesOrder.store?.storeGuid ?? '';
  const returnId = requestDetails?.id ?? '';

  const onCreateNote = useCallback(
    async (e: React.FormEvent<HTMLFormElement>) => {
      try {
        e.preventDefault();
        if (!value.trim()) {
          return;
        }

        Segment.trackEvent(RETURN_ACTION_NOTE_FORM_SUBMITTED, {
          type: RETURN_ACTION_NOTE_FORM_SUBMITTED,
          enhanced: true,
          requestId: returnId,
          storeGuid,
          note: value,
        });
        setIsProcessing(true);
        const newNote = await createReturnNote(storeGuid, returnId, value);
        onUpdateRequestDetails((data) => ({
          timeline: [
            {
              title: newNote?.note,
              recordedBy: newNote?.userName,
              recordedOn: newNote?.createdAt,
            },
            ...(data?.timeline || []),
          ],
        }));
        setValue('');
        NotificationService.showGenericSuccessNotification(<FormattedMessage {...messages.requestNoteCreateSuccessTitle} />);
      } catch {
        NotificationService.showGenericFailureNotification(<FormattedMessage {...messages.requestNoteCreateErrorTitle} />);
      } finally {
        setIsProcessing(false);
      }
    },
    [storeGuid, returnId, value, setIsProcessing, onUpdateRequestDetails, setValue],
  );

  const onChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => setValue(e?.target?.value), [setValue]);

  const onBlur = useCallback(() => setValue((v: string) => v.trim()), [setValue]);

  return (
    <form onSubmit={onCreateNote}>
      <FlexRow className={style['note-row']} gap={Gap.Space16}>
        <Input
          className={style['note-input']}
          disabled={isProcessing}
          label={<FormattedMessage {...messages.noteInputLabel} />}
          onBlur={onBlur}
          onChange={onChange}
          placeholder={intl.formatMessage(messages.noteInputPlaceholder)}
          value={value}
          version={version2}
        />
        <Button disabled={!value.trim() || isProcessing} isLoading={isProcessing} theme='tertiary' type='submit'>
          <FormattedMessage {...messages.addNoteButton} />
        </Button>
      </FlexRow>
    </form>
  );
};
